"use client";

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import React from "react";
import { LessonHeading, useLessonPresentation } from "./LessonPrimitives";

interface WhiteboardFocusSurfaceProps {
  children: React.ReactNode;
  className?: string;
  resolved?: boolean;
}

/** Shared visual hierarchy for the single dominant teaching target. */
export const WhiteboardFocusSurface: React.FC<WhiteboardFocusSurfaceProps> = ({
  children,
  className = "",
  resolved = false,
}) => {
  const lesson = useLessonPresentation();
  if (lesson)
    return (
      <LessonHeading
        as="div"
        data-whiteboard-focus-surface
        data-whiteboard-focus-resolved={resolved ? "true" : "false"}
        className={className}
      >
        {children}
      </LessonHeading>
    );
  return (
    <div
      data-whiteboard-focus-surface
      data-whiteboard-focus-resolved={resolved ? "true" : "false"}
      className={`rounded-xl border px-3 py-3 transition-colors duration-200 ${
        resolved ? "border-[#22C55E]/35 bg-[#22C55E]/10" : "border-[#FD954F]/30 bg-[#FD954F]/10"
      } ${className}`}
    >
      {children}
    </div>
  );
};

interface WhiteboardProgressiveRevealProps {
  show: boolean;
  children: React.ReactNode;
  className?: string;
}

/** Reveal supporting information in place while preserving the board layout. */
export const WhiteboardProgressiveReveal: React.FC<WhiteboardProgressiveRevealProps> = ({
  show,
  children,
  className = "",
}) => {
  const reducedMotion = useReducedMotion();

  return (
    <AnimatePresence initial={false}>
      {show && (
        <motion.div
          data-whiteboard-progressive-reveal
          initial={reducedMotion ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reducedMotion ? undefined : { opacity: 0, y: -4 }}
          transition={reducedMotion ? { duration: 0 } : { duration: 0.22, ease: "easeOut" }}
          className={`font-sans ${className}`}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
